import {
  Box,
  Dialog,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { usePremisesList } from "../hooks/usePremisesList";
import { useOptionalAuth, userCan } from "../auth/AuthContext";
import type { PermissionKey } from "@shared/types/auth";

export interface Command {
  id: string;
  label: string;
  group: string;
  href: string;
  keywords?: string;
  /**
   * Hide this entry unless the user holds the permission.
   *
   * The palette is a second front door to every page, so an entry left ungated
   * here is reachable even when the nav that guards it is hidden.
   */
  requirePermission?: PermissionKey;
  /**
   * Hide this entry unless the user is Nenda staff.
   *
   * For pages guarded by `adminOnly` in the nav rather than by a permission
   * key - the palette must not become the way around that gate.
   */
  adminOnly?: boolean;
}

export const NAV_COMMANDS: Command[] = [
  {
    id: "nav-premises",
    label: "Premises & Screens",
    group: "Go to",
    href: "/premises",
  },
  {
    id: "nav-companies",
    label: "Companies",
    group: "Go to",
    href: "/companies",
    adminOnly: true,
  },
  {
    id: "nav-analytics",
    label: "Overview · Fleet health",
    group: "Go to",
    href: "/analytics",
    keywords: "fleet devices analytics uptime",
  },
  {
    id: "nav-health-alerts",
    label: "Overview · Health alerting",
    group: "Go to",
    href: "/health-alerts",
    keywords: "alerts rules offline notify email",
  },
  {
    id: "nav-media",
    label: "Global media",
    group: "Go to",
    href: "/media",
    keywords: "media library assets images logos catalogue",
    // Nenda's own shelf. A customer has no business in it, and the nav entry
    // is gated the same way.
    requirePermission: "canManageMedia",
  },
  {
    id: "nav-content",
    label: "Content",
    group: "Go to",
    href: "/frontlayer/media",
    keywords: "media library assets images customer company premise innehall",
  },
  {
    id: "nav-home-screen",
    label: "Home screen",
    group: "Go to",
    href: "/frontlayer/home",
    keywords:
      "start navigation startvy menu tiles screens apps radio information",
  },
  {
    id: "nav-appearance",
    label: "Appearance",
    group: "Go to",
    href: "/frontlayer/appearance",
    // Nenda-side, like its menu entry.
    adminOnly: true,
    keywords: "template utseende colours typography background branding logo",
  },
  { id: "nav-users", label: "Users", group: "Go to", href: "/users" },
  {
    id: "nav-settings",
    label: "General settings",
    group: "Go to",
    href: "/settings/general",
  },
  {
    id: "nav-room-types",
    label: "Room types",
    group: "Go to",
    href: "/room-types",
  },
  {
    id: "nav-templates",
    label: "Templates",
    group: "Go to",
    href: "/templates",
  },
  { id: "nav-apps", label: "Apps", group: "Go to", href: "/apps" },
  { id: "nav-radio", label: "Radio", group: "Go to", href: "/radio-stations" },
  {
    id: "nav-distribution-channels",
    label: "Distribution · Channels",
    group: "Go to",
    href: "/distribution/channels",
    // Both old names stay searchable: an operator who learned "effect
    // channels" should still land here after the two were merged.
    keywords:
      "channels effect linear live loops autoplayer broadcast lineup streams iptv",
    // Same gate the nav and every Distribution route use. The palette is the
    // second front door, so it must move with them: gated by the permission
    // rather than by staff-ness, or a key-holder finds the menu hidden here
    // while the server would have let them in.
    requirePermission: "canManageDistribution",
  },
  {
    id: "nav-distribution-lineup",
    label: "Distribution · Line-up",
    group: "Go to",
    href: "/distribution/lineup",
    // "channel order" and "channel numbers" are what an operator will actually
    // type, and neither word is in the label. `numbers` is deliberate even
    // though the model emits none: it is what somebody looking for this asks
    // for, and landing them here is how they learn there are none.
    keywords:
      "lineup line-up channel order channel numbers sort ordering national svt tv4",
    requirePermission: "canManageDistribution",
  },
  {
    id: "nav-distribution-vod",
    label: "Distribution · VOD",
    group: "Go to",
    href: "/distribution/vod",
    keywords: "vod video on demand titles movies shelf",
    requirePermission: "canManageDistribution",
  },
  {
    id: "nav-distribution-carousels",
    label: "Distribution · Carousels",
    group: "Go to",
    href: "/distribution/carousels",
    keywords:
      "carousels rails rows home page shelf curated dynamic collection epg karusell",
    requirePermission: "canManageDistribution",
  },
  {
    id: "nav-distribution-products",
    label: "Distribution · Products",
    group: "Go to",
    href: "/distribution/products",
    keywords:
      "products offering feature set featureset price sold sell package grant entitlement upsales produkt erbjudande",
    requirePermission: "canManageDistribution",
  },
  {
    id: "nav-distribution-packages",
    label: "Distribution · Packages",
    group: "Go to",
    href: "/distribution/packages",
    keywords:
      "packages entitlement media collection rule channels curated pin paket",
    requirePermission: "canManageDistribution",
  },
  {
    id: "nav-distribution-providers",
    label: "Distribution · Content providers",
    group: "Go to",
    href: "/distribution/providers",
    keywords:
      "content providers suppliers trusted licence rule leverantor distributor scanbox",
    requirePermission: "canManageDistribution",
  },
  {
    id: "nav-distribution-submissions",
    label: "Distribution · Review queue",
    group: "Go to",
    href: "/distribution/submissions",
    keywords:
      "review queue submissions approve reject screening supplier upload granska",
    requirePermission: "canManageDistribution",
  },
  {
    id: "nav-distribution-bulk-ingest",
    label: "Distribution · Bulk ingest",
    group: "Go to",
    href: "/distribution/bulk-ingest",
    keywords: "bulk ingest upload jobs transcode package",
    requirePermission: "canManageDistribution",
  },
];

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);
export const PALETTE_EVENT = "foyer:command-palette";
export const PALETTE_HINT = isMac ? "⌘K" : "Ctrl K";

/**
 * Global ⌘K / Ctrl-K command palette: fuzzy-jump to any section or premise.
 * Opens on the hotkey or a `foyer:command-palette` window event (dispatched by
 * the sidebar trigger).
 */
export default function CommandPalette() {
  const navigate = useNavigate();
  const { premises } = usePremisesList();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    function onEvent() {
      setOpen(true);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener(PALETTE_EVENT, onEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(PALETTE_EVENT, onEvent);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  const user = useOptionalAuth()?.user ?? null;
  const commands = useMemo<Command[]>(() => {
    const prem: Command[] = premises.map((p) => ({
      id: `prem-${p.premiseId}`,
      label: p.premiseName,
      group: "Premises",
      href: `/premises/${p.premiseId}`,
      keywords: p.premiseId,
    }));
    const nav = NAV_COMMANDS.filter(
      (c) =>
        (!c.adminOnly || user?.type === "admin") &&
        (!c.requirePermission || userCan(user, c.requirePermission))
    );
    return [...nav, ...prem];
  }, [premises, user]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      `${c.label} ${c.keywords ?? ""} ${c.group}`.toLowerCase().includes(q)
    );
  }, [commands, query]);

  useEffect(() => setActive(0), [query]);

  function run(cmd: Command | undefined) {
    if (!cmd) return;
    navigate(cmd.href);
    setOpen(false);
  }

  function onInputKey(e: KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(filtered[active]);
    }
  }

  // Keep the active row in view as the user arrows through.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${active}"]`
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  let lastGroup = "";

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { position: "fixed", top: 80, m: 0 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <SearchIcon sx={{ color: "text.secondary" }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Jump to a page or premise…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKey}
          sx={{ fontSize: 16 }}
          inputProps={{ "data-testid": "command-palette-input" }}
        />
      </Box>
      <List ref={listRef} sx={{ maxHeight: 360, overflowY: "auto", py: 1 }}>
        {filtered.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No matches.
            </Typography>
          </Box>
        )}
        {filtered.map((cmd, i) => {
          const showGroup = cmd.group !== lastGroup;
          lastGroup = cmd.group;
          return (
            <Box key={cmd.id}>
              {showGroup && (
                <Typography
                  variant="overline"
                  sx={{ display: "block", px: 2, pt: 1 }}
                >
                  {cmd.group}
                </Typography>
              )}
              <ListItemButton
                data-index={i}
                selected={i === active}
                onClick={() => run(cmd)}
                onMouseEnter={() => setActive(i)}
                sx={{ mx: 1, borderRadius: 2 }}
              >
                <ListItemText primary={cmd.label} />
              </ListItemButton>
            </Box>
          );
        })}
      </List>
    </Dialog>
  );
}
